import React from "react";
import { Button as MuiButton, useTheme } from "@mui/material";
import type { ButtonProps } from "@mui/material/Button";
import type { SxProps, Theme } from "@mui/material";
import type { ElementType } from "react";
import { getTextGradient } from "../../constants/gradient";

export type ButtonVariant = "contained" | "outlined" | "text" | "elevated" | "icon";

interface ButtonComponentProps extends Omit<ButtonProps, "variant"> {
  variant?: ButtonVariant;
  sx?: SxProps<Theme>;
  to?: string;
  component?: ElementType;
}

const Button: React.FC<ButtonComponentProps> = ({
  children,
  size = "small",
  variant = "contained",
  sx,
  to,
  component,
  ...props
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const gradient = getTextGradient(theme);

  const getVariantStyles = (): SxProps<Theme> => {
    switch (variant) {
      case "contained":
        return {
          backgroundImage: isDark
            ? "linear-gradient(135deg, #818cf8 0%, #a5b4fc 50%, #c7d2fe 100%)"
            : "linear-gradient(135deg, #1e293b 0%, #3b82f6 100%)",
          color: isDark ? "#1e293b" : "#fff",
          border: "none",
          boxShadow: (theme) =>
            theme.palette.mode === "dark"
              ? "0 4px 16px rgba(129, 140, 248, 0.4)"
              : "0 4px 16px rgba(30, 41, 59, 0.3)",
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: "-100%",
            width: "100%",
            height: "100%",
            background: isDark
              ? "linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)"
              : "linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)",
            transition: "left 0.5s ease",
          },
          "&:hover": {
            backgroundImage: isDark
              ? "linear-gradient(135deg, #a5b4fc 0%, #c7d2fe 50%, #e0e7ff 100%)"
              : "linear-gradient(135deg, #0f172a 0%, #1e40af 100%)",
            color: isDark ? "#1e293b" : "#fff",
            textShadow: isDark ? "none" : "0 1px 2px rgba(0,0,0,0.2)",
            filter: "brightness(1.05)",
            "&::before": {
              left: "100%",
            },
          },
          "&:active": {
            boxShadow: (theme) =>
              theme.palette.mode === "dark"
                ? "0 2px 12px rgba(129, 140, 248, 0.4)"
                : "0 2px 12px rgba(30, 41, 59, 0.3)",
            filter: "brightness(0.95)",
          },
          "&:disabled": {
            backgroundImage: "none",
            backgroundColor: (theme) =>
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.1)"
                : "rgba(0,0,0,0.1)",
            color: (theme) =>
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.3)"
                : "rgba(0,0,0,0.3)",
            boxShadow: "none",
            filter: "none",
            "&::before": {
              display: "none",
            },
          },
        };

      case "outlined":
        return {
          border: (theme) =>
            `1px solid ${
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.35)"
                : "rgba(15,23,42,0.35)"
            }`,
          color: (theme) =>
            theme.palette.mode === "dark"
              ? "rgba(248,250,252,0.9)"
              : theme.palette.text.primary,
          backgroundColor: "transparent",
          boxShadow: "none",
          transition:
            "border-color 0.2s ease, background-color 0.2s ease, color 0.2s ease",
          "&:hover": {
            borderColor: (theme) =>
              theme.palette.mode === "dark"
                ? "rgba(165, 180, 252, 0.9)"
                : "rgba(59, 130, 246, 0.9)",
            backgroundColor: (theme) =>
              theme.palette.mode === "dark"
                ? "rgba(129, 140, 248, 0.08)"
                : "rgba(59, 130, 246, 0.08)",
            color: (theme) =>
              theme.palette.mode === "dark"
                ? "#c7d2fe"
                : theme.palette.primary.main,
            boxShadow: "none",
          },
          "&:active": {
            borderColor: (theme) =>
              theme.palette.mode === "dark"
                ? "rgba(129, 140, 248, 0.7)"
                : "rgba(37, 99, 235, 0.7)",
            backgroundColor: (theme) =>
              theme.palette.mode === "dark"
                ? "rgba(129, 140, 248, 0.14)"
                : "rgba(59, 130, 246, 0.14)",
          },
          "&:disabled": {
            borderColor: (theme) =>
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.12)"
                : "rgba(0,0,0,0.12)",
            color: (theme) =>
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.35)"
                : "rgba(0,0,0,0.35)",
          },
        };

      case "text":
        return {
          backgroundImage: gradient,
          backgroundClip: "text",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          color: "transparent",
          backgroundColor: "transparent",
          border: "none",
          "&:hover": {
            backgroundColor: (theme) =>
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.05)"
                : "rgba(0,0,0,0.05)",
            backgroundImage: gradient,
            backgroundClip: "text",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          },
          "&:active": {
            backgroundColor: (theme) =>
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.08)"
                : "rgba(0,0,0,0.08)",
          },
        };

      case "elevated":
        return {
          backgroundColor: (theme) => theme.palette.primary.main,
          color: "#fff",
          border: "none",
          boxShadow: (theme) =>
            theme.palette.mode === "dark"
              ? "0 8px 24px rgba(129, 140, 248, 0.5)"
              : "0 8px 24px rgba(59, 130, 246, 0.5)",
          "&:hover": {
            backgroundColor: (theme) => theme.palette.primary.dark,
            boxShadow: (theme) =>
              theme.palette.mode === "dark"
                ? "0 12px 32px rgba(129, 140, 248, 0.6)"
                : "0 12px 32px rgba(59, 130, 246, 0.6)",
          },
          "&:active": {
            boxShadow: (theme) =>
              theme.palette.mode === "dark"
                ? "0 4px 16px rgba(129, 140, 248, 0.5)"
                : "0 4px 16px rgba(59, 130, 246, 0.5)",
          },
          "&:disabled": {
            backgroundColor: (theme) =>
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.1)"
                : "rgba(0,0,0,0.1)",
            color: (theme) =>
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.3)"
                : "rgba(0,0,0,0.3)",
            boxShadow: "none",
          },
        };

      case "icon":
        return {
          minWidth: "auto",
          width: children ? "auto" : 40,
          height: 40,
          padding: children ? "8px 16px" : 0,
          borderRadius: children ? 20 : "50%",
          backgroundColor: (theme) =>
            theme.palette.mode === "dark"
              ? "rgba(255,255,255,0.1)"
              : "rgba(0,0,0,0.05)",
          color: "text.primary",
          border: "none",
          "&:hover": {
            backgroundColor: (theme) =>
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.15)"
                : "rgba(0,0,0,0.1)",
            boxShadow: (theme) =>
              theme.palette.mode === "dark"
                ? "0 2px 8px rgba(0,0,0,0.2)"
                : "0 2px 8px rgba(0,0,0,0.1)",
          },
          "&:active": {
            boxShadow: "none",
          },
        };

      default:
        return {};
    }
  };

  const baseSx: SxProps<Theme> = {
    borderRadius: variant === "icon" && !children ? "50%" : 20,
    textTransform: "none",
    fontWeight: 600,
    fontSize: "0.875rem",
    lineHeight: 1.5,
    letterSpacing: "0.02em",
    px: variant === "icon" && !children ? 0 : variant === "icon" ? 2 : 3,
    py: variant === "icon" && !children ? 0 : variant === "icon" ? 1 : 1.25,
    minWidth: "auto",
    transition: "all 0.2s ease",
    ...(variant === "contained" && {
      "& > *": {
        position: "relative",
        zIndex: 1,
      },
    }),
    ...getVariantStyles(),
  };

  const muiVariant: ButtonProps["variant"] =
    variant === "contained" || variant === "elevated"
      ? "contained"
      : variant === "outlined"
      ? "outlined"
      : "text";

  const combinedSx = Array.isArray(sx)
    ? [baseSx, ...sx]
    : sx
    ? [baseSx, sx]
    : [baseSx];

  return (
    <MuiButton
      size={size}
      variant={muiVariant}
      component={component ?? (to ? "a" : undefined)}
      {...(to ? { to } : {})}
      sx={combinedSx}
      {...props}
    >
      {children}
    </MuiButton>
  );
};

export default Button;
